import React, { useState, useMemo, useEffect } from "react";
import { Button, SwipeableDrawer, styled, Box, AccordionSummary, AccordionDetails, Accordion, Checkbox, FormControlLabel, FormLabel, Slider } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { genderFullOptions, languagesOptions, locationDropdownOptions, extraCurricularOptions, locationFilterOptions, genderOptions } from "../../../../constants";
import { useSelector } from "react-redux";
import { createSearchParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";

const CustomSwipeableDrawer = styled(SwipeableDrawer)`
    & .MuiDrawer-paperAnchorBottom {
        border-top-left-radius: 0.75rem !important;
        border-top-right-radius: 0.75rem !important;
        height: 80%;
    }
`;

const PrettoSlider = styled(Slider)({
    color: '#52af77',
    height: 5,
    padding: 10,
    '& .MuiSlider-track': {
        border: 'none',
    },
    '& .MuiSlider-valueLabel': {
        lineHeight: 1.2,
        fontSize: 12,
        background: 'unset',
        padding: 0,
        width: 32,
        height: 32,
        borderRadius: '50% 50% 50% 0',
        backgroundColor: '#000000',
        transformOrigin: 'bottom left',
        transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
        '&::before': { display: 'none' },
        '&.MuiSlider-valueLabelOpen': {
            transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
        },
        '& > *': {
            transform: 'rotate(45deg)',
        },
    },
});

const AccordionItem = ({ value, onChange, options, multiSelect = false }) => {
    const oldValue = value || [0, 75];

    const isValueEqual = (val) => {
        if (multiSelect) {
            return value.includes(val);
        } else {
            return value === val;
        }
    }

    const onChangeOptions = (selectedValue) => (event) => {
        if (multiSelect) {
            const index = value.indexOf(selectedValue)
            if (index === -1) onChange([ ...value, selectedValue ]);
            else onChange(value.filter((_, i) => i !== index))
        } else {
            if (value !== selectedValue) onChange(selectedValue);
            else onChange(undefined);
        }
    }

    const handleChange = (event, newValue, activeThumb) => {
        if (!Array.isArray(newValue)) return;
        if (activeThumb === 0) {
            onChange([Math.min(newValue[0], oldValue[1] - 1), oldValue[1]]);
        } else {
            onChange([oldValue[0], Math.max(newValue[1], oldValue[0] + 1)]);
        }
    };

    return (
        <>
            {options ? (
                <Box className="flex flex-col gap-1 max-h-[200px] overflow-auto">
                    {options?.map((item) => (
                        <Box className="flex justify-between max-w-[300px] w-full">
                            <FormLabel>{item.label}</FormLabel>
                            <Checkbox sx={{ '&.Mui-checked': { color: "#2F80ED" } }} checked={isValueEqual(item.value)} onChange={onChangeOptions(item.value)} name="jason" />
                        </Box>
                    ))}
                </Box>
            ) : (
                <Box className="max-w-[400px] w-full px-3">
                    <PrettoSlider
                        value={oldValue}
                        onChange={handleChange} className='mt-[50px] text-black'
                        disableSwap max={75} min={0} valueLabelDisplay="on"
                    />
                </Box>
            )}
        </>
    )
}

const ArtistFilter = ({ open, onClose }) => {
    const navigate = useNavigate();
    const webLocation = useLocation();
    const [expanded, setExpanded] = useState(-1);
    const [gender, setGender] = useState(undefined);
    const [profileCategory, setProfileCategory] = useState("");
    const [languages, setLanguages] = useState([]);
    const [location, setLocation] = useState([]);
    const [expertise, setExpertise] = useState([]);
    const [ageFilter, setAgeFilter] = useState([0, 75]);
    const categoryList = useSelector(state => state.recruiterGeneralReducer?.profileCategoryList) ?? [];

    const [queryParameters] = useSearchParams()

    const categoryOptions = useMemo(() => categoryList.map((item) => ({
        label: item.name, value: item.name
    })), [categoryList]);

    useEffect(() => {
        if (open) {
            setExpanded(-1)
            const categoryName = queryParameters.get("categoryName");
            const gender = queryParameters.get("gender");
            const includesLocation = queryParameters.get("includesLocation");
            const includesExpertise = queryParameters.get("includesExpertise");
            const inlcudesLanguage = queryParameters.get("inlcudesLanguage");
            const minAge = queryParameters.get("minAge");
            const maxAge = queryParameters.get("maxAge");

            setProfileCategory(categoryName ?? "");
            setGender(gender ?? "");
            setLocation(includesLocation?.split(",") ?? []);
            setExpertise(includesExpertise?.split(",") ?? []);
            setLanguages(inlcudesLanguage?.split(",") ?? []);
            const min = minAge ?? 0;
            const max = maxAge ?? 75;
            setAgeFilter([min, max]);
        }
    }, [queryParameters, open]);

    const handleChangeExpanded = (index) => (event, newExpanded) => {
        setExpanded(newExpanded ? index : -1);
    }

    const onSubmitFilter = async () => {
        const currentFilter = {}
        if (profileCategory) currentFilter.categoryName = profileCategory;
        if (location?.length > 0) currentFilter.includesLocation = location?.join(",");
        if (languages?.length > 0) currentFilter.inlcudesLanguage = languages?.join(",");
        if (expertise?.length > 0) currentFilter.includesExpertise = expertise?.join(",");
        if (gender) currentFilter.gender = gender;
        currentFilter.minAge = +ageFilter[0];
        currentFilter.maxAge = +ageFilter[1];
        const params = createSearchParams(currentFilter).toString();
        navigate({ pathname: webLocation.pathname, search: params });
        onClose();
    }

    return (
        <CustomSwipeableDrawer anchor="bottom" open={open} onClose={onClose}>
            <Box className="py-4 px-4" sx={{ width: 'auto' }}>
                <div className="flex justify-between items-center">
                    <Button onClick={onClose} className="text-[#474747] text-[14px] font-['Poppins-Medium'] font-[500] normal-case">Cancel</Button>
                    <h1 className="text-center text-[20px] leading-[22px] font-['Matter-Semibold']">Filters</h1>
                    <Button onClick={onSubmitFilter} className="text-[#2F80ED] text-[14px] font-['Poppins-Medium'] font-[500] normal-case">Done</Button>
                </div>
                <div className="py-2">
                    <Accordion expanded={expanded === 0} onChange={handleChangeExpanded(0)}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            Artist Category
                        </AccordionSummary>
                        <AccordionDetails>
                            <AccordionItem value={profileCategory} onChange={setProfileCategory} options={categoryOptions} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion expanded={expanded === 1} onChange={handleChangeExpanded(1)}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            Gender
                        </AccordionSummary>
                        <AccordionDetails>
                            <AccordionItem value={gender} onChange={setGender} options={genderFullOptions} />
                        </AccordionDetails>
                    </Accordion>
                    {/* <Accordion expanded={expanded === 2} onChange={handleChangeExpanded(2)}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            Age Range
                        </AccordionSummary>
                        <AccordionDetails>
                            <AccordionItem value={ageFilter} onChange={setAgeFilter} />
                        </AccordionDetails>
                    </Accordion> */}
                     <Box className="flex flex-col gap-2 px-4 py-2">
                        <div className="flex w-full justify-between items-center mr-2">
                            <h1 className="font-['Poppins-Regular'] font-[400] text-[14px] text-[#000]">Age Range</h1>
                            {/* <h2 className="font-['Poppins-Regular'] font-[400] text-[12px] text-[#9E9E9E] line-clamp-1 text-ellipsis max-w-[60%]" title={`${ageFilter[0]}-${ageFilter[1]}`}>{ageFilter[0]}-{ageFilter[1]}</h2> */}
                        </div>
                        <AccordionItem value={ageFilter} onChange={setAgeFilter} />
                    </Box>
                    <Accordion expanded={expanded === 3} onChange={handleChangeExpanded(3)}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            Location
                        </AccordionSummary>
                        <AccordionDetails>
                            <AccordionItem value={location} multiSelect onChange={setLocation} options={locationFilterOptions} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion expanded={expanded === 4} onChange={handleChangeExpanded(4)}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            Languages
                        </AccordionSummary>
                        <AccordionDetails>
                            <AccordionItem value={languages} multiSelect onChange={setLanguages} options={languagesOptions} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion expanded={expanded === 5} onChange={handleChangeExpanded(5)}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            Expertise
                        </AccordionSummary>
                        <AccordionDetails>
                            <AccordionItem value={expertise} multiSelect onChange={setExpertise} options={extraCurricularOptions} />
                        </AccordionDetails>
                    </Accordion>
                </div>
            </Box>
        </CustomSwipeableDrawer>
    );
}

export default React.memo(ArtistFilter, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open;
});